/**
 * IdleResourceKiller — Engine #28 v35.16
 *
 * Maximum-safety destructive flow:
 *   1. Click "Delete" or "Snap+Del" on a candidate
 *   2. Modal asks user to TYPE the resource id literal (GitHub-style)
 *   3. User clicks "Send email code" → backend mints challenge + emails the
 *      6-digit code via Resend. UI moves to step 2.
 *   4. User enters the 6-digit code from email + types resource id again
 *      (we keep the typed_resource_id stored from step 1 for backend match)
 *   5. UI calls /api/idle-killer/apply with confirm + typed_resource_id +
 *      challenge_id + mfa_code.  Server validates ALL gates before action.
 *
 * Plus:
 *   - Kill-switch banner at top of page (1-click 30d freeze).
 *   - Daily cap tile (count + $ used / cap).
 *   - shadcn AlertDialog for graduate / delete-policy (no more window.confirm).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Trash2, Tag, AlertTriangle, ShieldCheck, ArrowLeft, Plus,
  Database, Server, Network, HardDrive, Camera, Activity, CheckCircle2,
  Cloud, Mail, Lock, Unlock, ShieldOff,
} from 'lucide-react';
import { toast } from 'sonner';

import TeachOnEmpty from '../components/TeachOnEmpty';
import NextActionPill from '../components/NextActionPill';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const RESOURCE_TYPE_META = {
  detached_ebs:     { label: 'Detached EBS volumes',     icon: HardDrive, color: 'amber' },
  stopped_ec2:      { label: 'Stopped EC2 instances',    icon: Server,    color: 'sky' },
  unattached_eip:   { label: 'Unattached Elastic IPs',   icon: Network,   color: 'violet' },
  unused_elb:       { label: 'Unused Load Balancers',    icon: Network,   color: 'rose' },
  old_snapshot:     { label: 'Old EBS snapshots',        icon: Camera,    color: 'cyan' },
  idle_rds:         { label: 'Idle RDS databases',       icon: Database,  color: 'emerald' },
  idle_nat_gateway: { label: 'Idle NAT gateways',        icon: Cloud,     color: 'amber' },
};
const COLOR_CLS = {
  amber:   { ring: 'ring-amber-500/30',  text: 'text-amber-300',  bg: 'bg-amber-500/15' },
  sky:     { ring: 'ring-sky-500/30',    text: 'text-sky-300',    bg: 'bg-sky-500/15' },
  violet:  { ring: 'ring-violet-500/30', text: 'text-violet-300', bg: 'bg-violet-500/15' },
  rose:    { ring: 'ring-rose-500/30',   text: 'text-rose-300',   bg: 'bg-rose-500/15' },
  cyan:    { ring: 'ring-cyan-500/30',   text: 'text-cyan-300',   bg: 'bg-cyan-500/15' },
  emerald: { ring: 'ring-emerald-500/30', text: 'text-emerald-300', bg: 'bg-emerald-500/15' },
};
const fmtUsd = (n) => {
  const v = Number(n || 0);
  if (v >= 1000) return `$${(v / 1000).toFixed(1)}K`;
  if (v >= 100)  return `$${v.toFixed(0)}`;
  return `$${v.toFixed(2)}`;
};

export default function IdleResourceKiller() {
  const navigate = useNavigate();
  const [scan, setScan] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [stats, setStats] = useState(null);
  const [actions, setActions] = useState([]);
  const [resourceTypes, setResourceTypes] = useState(null);
  const [daily, setDaily] = useState(null);
  const [killSwitch, setKillSwitch] = useState(null);
  const [loading, setLoading] = useState(true);

  // New-policy form
  const [newType, setNewType] = useState('detached_ebs');
  const [newThreshold, setNewThreshold] = useState(30);
  const [newAction, setNewAction] = useState('tag_for_review');
  const [newExcluded, setNewExcluded] = useState('Environment=prod,KeepAlive=true');
  const [newMinSafety, setNewMinSafety] = useState(70);
  const [creating, setCreating] = useState(false);

  // Destructive flow modal
  const [destructive, setDestructive] = useState(null); // {candidate, action, step, typedId, code, challengeId, sending}

  // Confirm dialogs
  const [confirmGraduate, setConfirmGraduate] = useState(null); // policy obj
  const [confirmDeletePolicy, setConfirmDeletePolicy] = useState(null); // policy obj
  const [confirmKillSwitch, setConfirmKillSwitch] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, p, st, ac, rt, du, ks] = await Promise.all([
        axios.post(`${API}/idle-killer/scan`, {}, auth()),
        axios.get(`${API}/idle-killer/policies`, auth()),
        axios.get(`${API}/idle-killer/stats`, auth()),
        axios.get(`${API}/idle-killer/actions?limit=20`, auth()),
        axios.get(`${API}/idle-killer/resource-types`),
        axios.get(`${API}/idle-killer/daily-usage`, auth()),
        axios.get(`${API}/idle-killer/kill-switch`, auth()),
      ]);
      setScan(s.data);
      setPolicies(p.data.items || []);
      setStats(st.data);
      setActions(ac.data.items || []);
      setResourceTypes(rt.data);
      setDaily(du.data);
      setKillSwitch(ks.data);
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) navigate('/login');
      else toast.error(e?.response?.data?.detail || 'Failed to load Idle Resource Optimizer data');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onCreatePolicy = async () => {
    setCreating(true);
    try {
      const filters = newExcluded.split(',').map((s) => s.trim()).filter(Boolean);
      await axios.post(`${API}/idle-killer/policies`, {
        resource_type: newType,
        threshold_days: Number(newThreshold) || 30,
        action: newAction,
        excluded_tag_filters: filters,
        min_safety_score: Number(newMinSafety) || 70,
      }, auth());
      toast.success(`Policy saved for ${RESOURCE_TYPE_META[newType]?.label || newType}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save policy');
    } finally { setCreating(false); }
  };

  const onDeletePolicy = async (policy) => {
    try {
      await axios.delete(`${API}/idle-killer/policies/${policy.policy_id}`, auth());
      toast.success('Policy deleted');
      setConfirmDeletePolicy(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  const onGraduate = async (policy) => {
    try {
      await axios.post(`${API}/idle-killer/policies/${policy.policy_id}/graduate`, {}, auth());
      toast.success('Policy activated — cleanups will now run when you confirm each one');
      setConfirmGraduate(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Activate failed'); }
  };

  // ── Non-destructive (tag_for_review) — single click, no email gate ─────
  const onApplyTag = async (candidate) => {
    try {
      const { data } = await axios.post(`${API}/idle-killer/apply`, {
        resource_id: candidate.resource_id,
        resource_type: candidate.resource_type,
        action: 'tag_for_review',
        confirm: true,
      }, auth());
      if (data.shadow_mode) toast.success('Review mode: would have tagged for review (no real action)');
      else toast.success(`Tagged ${candidate.resource_id} for review`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Tag failed'); }
  };

  // ── Destructive flow — opens the 2-step modal ──────────────────────────
  const openDestructiveModal = (candidate, action) => {
    setDestructive({
      candidate, action, step: 'type',
      typedId: '', code: '', challengeId: null, sending: false,
    });
  };

  const sendDeleteCode = async () => {
    if (!destructive) return;
    if (destructive.typedId.trim() !== destructive.candidate.resource_id.trim()) {
      toast.error('Type the exact resource ID to continue');
      return;
    }
    setDestructive({ ...destructive, sending: true });
    try {
      const { data } = await axios.post(`${API}/idle-killer/request-delete-code`, {
        resource_id: destructive.candidate.resource_id,
        resource_type: destructive.candidate.resource_type,
        action: destructive.action,
      }, auth());
      toast.success(`6-digit code sent to ${data.email_destination_hint || 'your email'}`);
      setDestructive({
        ...destructive, sending: false, step: 'code',
        challengeId: data.challenge_id,
      });
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to send code';
      toast.error(msg);
      setDestructive({ ...destructive, sending: false });
    }
  };

  const submitDestructiveAction = async () => {
    if (!destructive || !destructive.challengeId) return;
    if ((destructive.code || '').length !== 6) {
      toast.error('Enter the 6-digit code from email');
      return;
    }
    setDestructive({ ...destructive, sending: true });
    try {
      const { data } = await axios.post(`${API}/idle-killer/apply`, {
        resource_id: destructive.candidate.resource_id,
        resource_type: destructive.candidate.resource_type,
        action: destructive.action,
        confirm: true,
        typed_resource_id: destructive.typedId,
        challenge_id: destructive.challengeId,
        mfa_code: destructive.code,
      }, auth());
      if (data.shadow_mode) toast.success(`Review mode: would have ${destructive.action.replace(/_/g, ' ')} (no real action)`);
      else toast.success(`Cleanup complete on ${destructive.candidate.resource_id}`);
      setDestructive(null);
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Action failed';
      toast.error(msg);
      setDestructive({ ...destructive, sending: false });
    }
  };

  // ── Kill switch ────────────────────────────────────────────────────────
  const onFreezeAccount = async () => {
    try {
      await axios.post(`${API}/idle-killer/kill-switch`, {
        days: 30, reason: 'User-initiated safety pause from UI',
      }, auth());
      toast.success('Cleanup paused for 30 days');
      setConfirmKillSwitch(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Pause failed'); }
  };

  const onUnfreezeAccount = async () => {
    try {
      await axios.delete(`${API}/idle-killer/kill-switch`, auth());
      toast.success('Cleanup resumed');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Resume failed'); }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="idle-killer-page">
      <Helmet><title>Idle Resource Optimizer · CARTIEAI</title></Helmet>

      <nav className="sticky top-0 z-50 backdrop-blur-md bg-slate-950/80 border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-slate-400 hover:text-emerald-300 text-sm" data-testid="idle-killer-back">
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </Link>
          <div className="flex items-center gap-3">
            <NextActionPill />
            <div className="text-xs text-slate-500 font-mono hidden sm:block">/idle-killer · Idle Resource Optimizer</div>
          </div>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        {/* Safety-pause banner */}
        {killSwitch?.frozen ? (
          <div className="rounded-xl bg-amber-950/60 ring-1 ring-amber-500/40 p-4 flex items-start gap-3" data-testid="kill-switch-banner-frozen">
            <ShieldOff className="w-5 h-5 text-amber-300 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-amber-200">Cleanup is paused</p>
              <p className="text-xs text-amber-300/80 mt-0.5">
                Until {new Date(killSwitch.frozen_until).toLocaleString()}. Reason: {killSwitch.reason || '—'}.
                Tag-for-review still works while paused.
              </p>
            </div>
            <Button onClick={onUnfreezeAccount} variant="outline" size="sm" className="text-amber-200 border-amber-500/40" data-testid="unfreeze-btn">
              <Unlock className="w-3.5 h-3.5 mr-1.5" /> Resume
            </Button>
          </div>
        ) : (
          <div className="rounded-xl bg-slate-900 ring-1 ring-white/10 p-4 flex items-center gap-3" data-testid="kill-switch-banner-active">
            <Lock className="w-4 h-4 text-emerald-400 shrink-0" />
            <div className="flex-1 text-xs text-slate-400">
              <span className="font-semibold text-slate-200">Safety pause ready.</span> Click <strong className="text-amber-300">Pause</strong> to halt all cleanup actions for 30 days. Tag-for-review still works while paused.
            </div>
            <Button onClick={() => setConfirmKillSwitch(true)} variant="outline" size="sm" className="text-amber-300 border-amber-500/30" data-testid="freeze-btn">
              <Lock className="w-3.5 h-3.5 mr-1.5" /> Pause 30 days
            </Button>
          </div>
        )}

        {/* Hero */}
        <div className="rounded-2xl bg-slate-900 bg-gradient-to-br from-emerald-500/10 via-slate-900 to-slate-900 ring-1 ring-emerald-500/30 p-6 sm:p-8" data-testid="idle-killer-hero">
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-[10px] font-mono uppercase tracking-widest mb-3">
            <Trash2 className="w-3 h-3" /> Idle Resource Optimizer · v35.18
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight" data-testid="idle-killer-h1">
            Find idle cloud spend. <span className="text-emerald-400">Clean it up safely.</span>
          </h1>
          <p className="mt-3 text-sm sm:text-base text-slate-400 max-w-2xl leading-relaxed">
            Every cleanup needs five hard gates: type-the-resource-ID confirm, a 6-digit email code, a safety-score check, an active safety pause status, and remaining daily cap.
            <strong className="text-emerald-300"> Nothing is ever removed without you clicking through all five per resource.</strong>
          </p>
          <div className="mt-4">
            <Link to="/idle-killer/connect" className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-500/15 ring-1 ring-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25 text-xs font-mono" data-testid="connect-aws-cta">
              <Cloud className="w-3.5 h-3.5" /> Connect your AWS account (read-only first) →
            </Link>
          </div>

          <div className="mt-6 grid sm:grid-cols-4 gap-3" data-testid="idle-killer-tiles">
            <div className="rounded-xl bg-slate-950 ring-1 ring-white/10 p-4">
              <div className="text-[10px] uppercase tracking-wider font-mono text-slate-500">Candidates</div>
              <div className="text-2xl font-extrabold text-white mt-1 font-mono tabular-nums" data-testid="tile-candidates">
                {loading ? '—' : (scan?.candidate_count || 0).toLocaleString()}
              </div>
            </div>
            <div className="rounded-xl bg-slate-950 ring-1 ring-emerald-500/30 p-4">
              <div className="text-[10px] uppercase tracking-wider font-mono text-emerald-300">Monthly savings (idle)</div>
              <div className="text-2xl font-extrabold text-emerald-300 mt-1 font-mono tabular-nums" data-testid="tile-monthly-savings">
                {loading ? '—' : fmtUsd(scan?.total_monthly_savings_usd || 0)}
              </div>
            </div>
            <div className="rounded-xl bg-slate-950 ring-1 ring-white/10 p-4">
              <div className="text-[10px] uppercase tracking-wider font-mono text-slate-500">Daily cap used</div>
              <div className="text-2xl font-extrabold text-white mt-1 font-mono tabular-nums" data-testid="tile-daily-cap">
                {loading ? '—' : `${daily?.killed_count || 0}/${daily?.count_cap || 25}`}
              </div>
              <div className="text-[10px] text-slate-500 font-mono mt-0.5">
                {fmtUsd(daily?.killed_dollar_amount || 0)} / {fmtUsd(daily?.dollar_cap || 500)}
              </div>
            </div>
            <div className="rounded-xl bg-slate-950 ring-1 ring-white/10 p-4">
              <div className="text-[10px] uppercase tracking-wider font-mono text-slate-500">Annualized</div>
              <div className="text-2xl font-extrabold text-white mt-1 font-mono tabular-nums" data-testid="tile-annualized">
                {loading ? '—' : fmtUsd(stats?.annualized_savings_usd || 0)}
              </div>
            </div>
          </div>

          {/* Per-type rollup */}
          {!loading && scan?.by_resource_type?.length > 0 && (
            <div className="mt-5 flex flex-wrap gap-2" data-testid="rollup-by-type">
              {scan.by_resource_type.map((r) => {
                const meta = RESOURCE_TYPE_META[r.resource_type] || {};
                const cls = COLOR_CLS[meta.color] || COLOR_CLS.emerald;
                const Icon = meta.icon || Activity;
                return (
                  <div key={r.resource_type} className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-950 ring-1 ${cls.ring}`}>
                    <Icon className={`w-3 h-3 ${cls.text}`} />
                    <span className="text-[11px] font-mono text-slate-300">{meta.label || r.resource_type}</span>
                    <span className="text-[10px] font-mono text-slate-500">·</span>
                    <span className="text-[11px] font-bold text-slate-100">{r.count}</span>
                    <span className={`text-[11px] font-mono ${cls.text}`}>{fmtUsd(r.monthly_savings_usd)}/mo</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Policy editor */}
        <Card className="bg-slate-900 border-white/10" data-testid="policy-editor">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h2 className="text-lg font-bold text-slate-100 flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-emerald-400" /> Cleanup policies
              </h2>
              <Badge className="bg-amber-500/15 border-amber-500/30 text-amber-300 text-[10px]">
                14-day review mode by default
              </Badge>
            </div>

            {policies.length > 0 && (
              <div className="space-y-2" data-testid="policy-list">
                {policies.map((p) => {
                  const meta = RESOURCE_TYPE_META[p.resource_type] || {};
                  const Icon = meta.icon || Activity;
                  return (
                    <div key={p.policy_id} className="rounded-lg bg-slate-950 ring-1 ring-white/10 p-3 flex items-center gap-3 flex-wrap" data-testid={`policy-row-${p.resource_type}`}>
                      <Icon className="w-4 h-4 text-emerald-400 shrink-0" />
                      <div className="text-sm font-semibold text-slate-100 flex-1">{meta.label || p.resource_type}</div>
                      <span className="text-[11px] font-mono text-slate-400">≥ {p.threshold_days}d</span>
                      <span className="text-[11px] font-mono text-slate-400">safety ≥ {p.min_safety_score}</span>
                      <Badge className={`${p.shadow_mode_active ? 'bg-amber-500/15 border-amber-500/30 text-amber-300' : 'bg-emerald-500/15 border-emerald-500/30 text-emerald-300'} text-[10px]`}>
                        {p.shadow_mode_active ? 'Review' : 'Active'} · {p.action.replace(/_/g, ' ')}
                      </Badge>
                      {p.shadow_mode_active && (
                        <Button size="sm" variant="outline" onClick={() => setConfirmGraduate(p)} className="text-[11px] h-7" data-testid={`graduate-${p.resource_type}`}>
                          Activate
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => setConfirmDeletePolicy(p)} className="text-[11px] h-7 text-rose-400 hover:text-rose-300" data-testid={`delete-policy-${p.resource_type}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="rounded-lg bg-slate-950 ring-1 ring-white/10 p-4 space-y-3" data-testid="new-policy-form">
              <div className="text-[10px] uppercase tracking-wider font-mono text-slate-500">New policy</div>
              <div className="grid sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-[10px] uppercase font-mono text-slate-500 mb-1">Resource type</label>
                  <select
                    value={newType}
                    onChange={(e) => {
                      setNewType(e.target.value);
                      const d = resourceTypes?.resource_types?.find((t) => t.type === e.target.value);
                      if (d) setNewThreshold(d.default_threshold_days);
                    }}
                    className="w-full rounded bg-slate-900 ring-1 ring-white/10 text-sm text-slate-100 px-3 py-2 font-mono"
                    data-testid="select-resource-type"
                  >
                    {(resourceTypes?.resource_types || []).map((t) => (
                      <option key={t.type} value={t.type}>{RESOURCE_TYPE_META[t.type]?.label || t.type}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] uppercase font-mono text-slate-500 mb-1">Threshold days</label>
                  <Input type="number" min="1" max="3650" value={newThreshold} onChange={(e) => setNewThreshold(e.target.value)} className="bg-slate-900 ring-1 ring-white/10 border-0 text-slate-100 font-mono" data-testid="input-threshold-days" />
                </div>
                <div>
                  <label className="block text-[10px] uppercase font-mono text-slate-500 mb-1">Action</label>
                  <select value={newAction} onChange={(e) => setNewAction(e.target.value)} className="w-full rounded bg-slate-900 ring-1 ring-white/10 text-sm text-slate-100 px-3 py-2 font-mono" data-testid="select-action">
                    {(resourceTypes?.valid_actions || ['tag_for_review', 'snapshot_then_delete', 'delete', 'notify_only']).map((a) => (
                      <option key={a} value={a}>{a.replace(/_/g, ' ')}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2">
                  <label className="block text-[10px] uppercase font-mono text-slate-500 mb-1">Excluded tag filters (comma-separated K=V)</label>
                  <Input value={newExcluded} onChange={(e) => setNewExcluded(e.target.value)} className="bg-slate-900 ring-1 ring-white/10 border-0 text-slate-100 font-mono text-xs" placeholder="Environment=prod,KeepAlive=true" data-testid="input-excluded-tags" />
                </div>
                <div>
                  <label className="block text-[10px] uppercase font-mono text-slate-500 mb-1">Min safety score (0-100)</label>
                  <Input type="number" min="0" max="100" value={newMinSafety} onChange={(e) => setNewMinSafety(e.target.value)} className="bg-slate-900 ring-1 ring-white/10 border-0 text-slate-100 font-mono" data-testid="input-min-safety" />
                </div>
              </div>
              <Button onClick={onCreatePolicy} disabled={creating} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold" data-testid="save-policy-btn">
                <Plus className="w-4 h-4 mr-2" /> {creating ? 'Saving...' : 'Save policy'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Candidate ledger */}
        <Card className="bg-slate-900 border-white/10" data-testid="candidate-ledger">
          <CardContent className="p-5">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <h2 className="text-lg font-bold text-slate-100 flex items-center gap-2">
                <Activity className="w-5 h-5 text-emerald-400" /> Live candidates
              </h2>
              <div className="text-[11px] text-slate-500 font-mono">
                {scan?.data_source === 'mock' ? 'demo data · deterministic' : scan?.data_source || ''}
              </div>
            </div>
            {loading && <div className="text-sm text-slate-500">Loading candidates...</div>}
            {!loading && (scan?.candidates || []).length === 0 && (
              <TeachOnEmpty
                title="No cleanup candidates match your current policies yet."
                body="Loosen the policy filters above, or connect a real AWS account to discover what's actually idle."
                ctaLabel="Connect AWS"
                ctaHref="/idle-killer/connect"
                estimatedTime="5 minutes"
                testid="empty-candidates"
              />
            )}
            {!loading && (scan?.candidates || []).length > 0 && (
              <div className="space-y-2" data-testid="candidate-list">
                {(scan.candidates || []).slice(0, 30).map((c) => {
                  const meta = RESOURCE_TYPE_META[c.resource_type] || {};
                  const cls = COLOR_CLS[meta.color] || COLOR_CLS.emerald;
                  const Icon = meta.icon || Activity;
                  const safetyColor = c.safety_score >= 80 ? 'text-emerald-400' :
                                      c.safety_score >= 50 ? 'text-amber-300' : 'text-rose-400';
                  return (
                    <div key={c.resource_id} className="rounded-lg bg-slate-950 ring-1 ring-white/10 p-3 flex items-center gap-3 flex-wrap" data-testid={`candidate-${c.resource_id}`}>
                      <div className={`h-8 w-8 rounded-md flex items-center justify-center ${cls.bg}`}>
                        <Icon className={`w-4 h-4 ${cls.text}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-mono text-slate-100 truncate">{c.resource_id}</div>
                        <div className="text-[10px] text-slate-500 font-mono">{c.region} · {c.account_id} · age {c.age_days}d</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-sm font-mono font-bold text-emerald-300">{fmtUsd(c.est_monthly_savings_usd)}/mo</div>
                        <div className={`text-[10px] font-mono ${safetyColor}`}>safety {c.safety_score}</div>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <Button size="sm" variant="outline" onClick={() => onApplyTag(c)} className="text-[10px] h-7 px-2" data-testid={`tag-${c.resource_id}`}>
                          <Tag className="w-3 h-3 mr-1" /> Tag
                        </Button>
                        {(c.resource_type === 'detached_ebs' || c.resource_type === 'idle_rds') && (
                          <Button size="sm" variant="outline" onClick={() => openDestructiveModal(c, 'snapshot_then_delete')} className="text-[10px] h-7 px-2 text-amber-300 hover:text-amber-200" data-testid={`snapshot-${c.resource_id}`}>
                            <Camera className="w-3 h-3 mr-1" /> Snap + Clean
                          </Button>
                        )}
                        <Button size="sm" variant="outline" disabled={c.safety_score < 50}
                                title={c.safety_score < 50 ? 'Safety score too low to clean up. Tag for review instead.' : 'Permanently clean up (requires email code)'}
                                onClick={() => openDestructiveModal(c, 'delete')}
                                className="text-[10px] h-7 px-2 text-amber-300 hover:text-amber-200 disabled:opacity-40" data-testid={`delete-${c.resource_id}`}>
                          <Trash2 className="w-3 h-3 mr-1" /> Clean up
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent actions */}
        {actions.length > 0 && (
          <Card className="bg-slate-900 border-white/10" data-testid="recent-actions">
            <CardContent className="p-5">
              <h2 className="text-lg font-bold text-slate-100 flex items-center gap-2 mb-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-400" /> Recent actions
              </h2>
              <div className="space-y-1.5">
                {actions.slice(0, 15).map((a) => (
                  <div key={a._id} className="flex items-center justify-between text-xs border-b border-white/5 last:border-0 pb-1.5" data-testid={`action-${a._id}`}>
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span className="text-[10px] text-slate-500 font-mono shrink-0">{new Date(a.created_at).toLocaleString()}</span>
                      <span className="font-mono text-slate-300 truncate">{a.resource_id}</span>
                      <Badge className={`${a.shadow_mode ? 'bg-amber-500/15 border-amber-500/30 text-amber-300' : 'bg-emerald-500/15 border-emerald-500/30 text-emerald-300'} px-1.5 py-0 text-[9px]`}>
                        {a.shadow_mode ? 'shadow' : 'live'} · {a.action.replace(/_/g, ' ')}
                      </Badge>
                    </div>
                    <span className="text-emerald-400 font-mono font-bold shrink-0">{fmtUsd(a.est_monthly_savings_usd)}/mo</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Honesty footer */}
        <div className="rounded-xl bg-slate-900 ring-1 ring-amber-500/20 p-4 text-xs text-slate-400" data-testid="honesty-note">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-amber-200 mb-1">Demo data on this account.</p>
              <p>The candidates above are deterministically generated for the demo user. Real customers see their own AWS/Azure/GCP resources after connecting via{' '}
                <Link to="/idle-killer/connect" className="text-emerald-400 hover:text-emerald-300 underline">/idle-killer/connect</Link>.
                During review mode nothing is removed, and even after a policy is active each cleanup still needs all five gates (type-id + email-code + safety + cap + active safety pause).</p>
            </div>
          </div>
        </div>
      </main>

      {/* ─── Destructive 2-step modal ────────────────────────────────────── */}
      <Dialog open={!!destructive} onOpenChange={(o) => !o && setDestructive(null)}>
        <DialogContent className="bg-slate-950 border-amber-500/30 text-slate-100" data-testid="destructive-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-300">
              <AlertTriangle className="w-5 h-5" />
              {destructive?.action === 'snapshot_then_delete' ? 'Snapshot + Cleanup' : 'Permanent Cleanup'}
            </DialogTitle>
            <DialogDescription className="text-slate-400 pt-2">
              {destructive?.step === 'type' && (
                <span>
                  You&apos;re about to {destructive.action === 'snapshot_then_delete' ? 'take a final snapshot then clean up' : 'permanently clean up'}{' '}
                  <code className="text-amber-300 font-mono">{destructive.candidate.resource_id}</code>. <br />
                  Type the resource ID below to confirm, then we&apos;ll email you a 6-digit code.
                </span>
              )}
              {destructive?.step === 'code' && (
                <span>
                  We just emailed a 6-digit code. Enter it below to complete the cleanup.
                  Code expires in 10 minutes.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          {destructive?.step === 'type' && (
            <div className="space-y-3" data-testid="destructive-step-type">
              <div className="rounded-lg bg-slate-900 ring-1 ring-white/10 p-3 font-mono text-sm">
                <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Resource</div>
                <div className="text-slate-100">{destructive.candidate.resource_id}</div>
                <div className="text-[10px] text-slate-500 mt-1">{destructive.candidate.resource_type} · {destructive.candidate.region} · est. {fmtUsd(destructive.candidate.est_monthly_savings_usd)}/mo</div>
              </div>
              <div>
                <label className="block text-[10px] uppercase font-mono text-slate-500 mb-1">Type the resource ID to confirm</label>
                <Input
                  value={destructive.typedId}
                  onChange={(e) => setDestructive({ ...destructive, typedId: e.target.value })}
                  placeholder={destructive.candidate.resource_id}
                  className="bg-slate-900 ring-1 ring-rose-500/40 border-0 text-slate-100 font-mono"
                  autoFocus
                  data-testid="destructive-typed-input"
                />
              </div>
            </div>
          )}

          {destructive?.step === 'code' && (
            <div className="space-y-3" data-testid="destructive-step-code">
              <div className="rounded-lg bg-emerald-500/5 ring-1 ring-emerald-500/20 p-3 flex items-center gap-2">
                <Mail className="w-4 h-4 text-emerald-400" />
                <span className="text-xs text-emerald-300">6-digit code sent to your email.</span>
              </div>
              <div>
                <label className="block text-[10px] uppercase font-mono text-slate-500 mb-1">Email code</label>
                <Input
                  value={destructive.code}
                  onChange={(e) => setDestructive({ ...destructive, code: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                  placeholder="000000"
                  maxLength={6}
                  className="bg-slate-900 ring-1 ring-emerald-500/40 border-0 text-slate-100 font-mono text-2xl text-center tracking-[0.4em]"
                  autoFocus
                  data-testid="destructive-code-input"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDestructive(null)} data-testid="destructive-cancel">Cancel</Button>
            {destructive?.step === 'type' && (
              <Button onClick={sendDeleteCode} disabled={destructive?.sending} className="bg-rose-500 hover:bg-rose-400 text-white font-bold" data-testid="destructive-send-code">
                {destructive?.sending ? 'Sending...' : 'Send email code'}
              </Button>
            )}
            {destructive?.step === 'code' && (
              <Button onClick={submitDestructiveAction} disabled={destructive?.sending || destructive?.code?.length !== 6} className="bg-rose-500 hover:bg-rose-400 text-white font-bold" data-testid="destructive-submit">
                {destructive?.sending ? 'Working...' : 'Confirm & execute'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Graduate confirmation ───────────────────────────────────────── */}
      <AlertDialog open={!!confirmGraduate} onOpenChange={(o) => !o && setConfirmGraduate(null)}>
        <AlertDialogContent className="bg-slate-950 border-amber-500/30 text-slate-100" data-testid="graduate-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-amber-300">Activate this cleanup policy?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              CARTIE will start performing the {confirmGraduate?.action?.replace(/_/g, ' ')} action on matching {RESOURCE_TYPE_META[confirmGraduate?.resource_type]?.label || confirmGraduate?.resource_type}.
              Each cleanup still requires the email-code + type-id confirmation per resource — nothing happens silently.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="graduate-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => confirmGraduate && onGraduate(confirmGraduate)} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold" data-testid="graduate-confirm">
              Activate policy
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ─── Delete-policy confirmation ──────────────────────────────────── */}
      <AlertDialog open={!!confirmDeletePolicy} onOpenChange={(o) => !o && setConfirmDeletePolicy(null)}>
        <AlertDialogContent className="bg-slate-950 border-amber-500/30 text-slate-100" data-testid="delete-policy-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-amber-300">Remove this cleanup policy?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              CARTIE will stop surfacing candidates for {RESOURCE_TYPE_META[confirmDeletePolicy?.resource_type]?.label || confirmDeletePolicy?.resource_type}. You can re-add it any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-policy-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => confirmDeletePolicy && onDeletePolicy(confirmDeletePolicy)} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold" data-testid="delete-policy-confirm">
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ─── Safety-pause confirmation ───────────────────────────────────── */}
      <AlertDialog open={confirmKillSwitch} onOpenChange={setConfirmKillSwitch}>
        <AlertDialogContent className="bg-slate-950 border-amber-500/30 text-slate-100" data-testid="kill-switch-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-amber-300 flex items-center gap-2"><Lock className="w-5 h-5" /> Pause cleanup for 30 days?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              No cleanup action will run until you resume.
              Tag-for-review still works while paused.
              You can resume anytime.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="kill-switch-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onFreezeAccount} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold" data-testid="kill-switch-confirm">
              <Lock className="w-3.5 h-3.5 mr-1.5" /> Pause 30 days
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
